import React from 'react';
import {
    Show,
    SimpleShowLayout,
    TextField,
    DateField,
    FunctionField,
    useTranslate,
    useRecordContext,
    useRefresh,
    useRedirect,
} from 'react-admin';
import {
    Box,
    Typography,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Card,
    CardContent,
    Button,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Cancel,
    Refresh,
    CloudDownload,
    CloudDone,
    Error as ErrorIcon,
    Pending,
    CloudUpload,
    Memory,
    Devices,
    Assessment,
    ArrowBack,
} from '@mui/icons-material';

// Status icon component
const StatusIcon = ({ status }: { status: string }) => {
    const normalizedStatus = status.toLowerCase();
    switch (normalizedStatus) {
        case 'pending':
            return <Pending fontSize="small" color="action" />;
        case 'sent':
            return <CloudUpload fontSize="small" color="primary" />;
        case 'in_progress':
            return <CloudUpload fontSize="small" color="primary" />;
        case 'downloading':
            return <CloudDownload fontSize="small" color="primary" />;
        case 'downloaded':
            return <CloudDone fontSize="small" color="success" />;
        case 'installing':
            return <CloudUpload fontSize="small" color="primary" />;
        case 'installed':
            return <CloudDone fontSize="small" color="success" />;
        case 'completed':
            return <CloudDone fontSize="small" color="success" />;
        case 'installationfailed':
        case 'installation_failed':
        case 'downloadfailed':
        case 'download_failed':
        case 'failed':
            return <ErrorIcon fontSize="small" color="error" />;
        case 'cancelled':
            return <Cancel fontSize="small" color="action" />;
        default:
            return <Pending fontSize="small" />;
    }
};

// Status chip component
const StatusChip = ({ status }: { status: string }) => {
    const translate = useTranslate();
    const normalizedStatus = status.toLowerCase();
    let color: 'default' | 'primary' | 'success' | 'error' | 'warning' = 'default';

    switch (normalizedStatus) {
        case 'pending':
        case 'sent':
            color = 'default';
            break;
        case 'in_progress':
        case 'downloading':
        case 'installing':
            color = 'primary';
            break;
        case 'completed':
        case 'downloaded':
        case 'installed':
            color = 'success';
            break;
        case 'failed':
        case 'installationfailed':
        case 'downloadfailed':
            color = 'error';
            break;
        case 'cancelled':
            color = 'warning';
            break;
    }

    return (
        <Chip
            label={translate(`resources.firmware-updates.statuses.${normalizedStatus}`)}
            color={color}
            size="small"
            icon={<StatusIcon status={normalizedStatus} />}
        />
    );
};

// Charger status table
const ChargerStatusTable = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (!record || !record.charger_statuses) {
        return null;
    }

    return (
        <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Devices sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                        {translate('resources.firmware-updates.charger_status.title')}
                    </Typography>
                </Box>

                {isMobile ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {record.charger_statuses.map((chargerStatus: any) => (
                            <Box key={chargerStatus.charger_id} sx={{ p: 1.5, border: '1px solid #E0E0E0', borderRadius: 2, overflow: 'hidden' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                        {chargerStatus.charger?.name || chargerStatus.charger_id}
                                    </Typography>
                                    <Box sx={{ flexShrink: 0 }}>
                                        <StatusChip status={chargerStatus.status || 'pending'} />
                                    </Box>
                                </Box>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                    {chargerStatus.charger?.sites?.name || '-'}
                                </Typography>
                                {chargerStatus.last_status_update_at && (
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                        {new Date(chargerStatus.last_status_update_at).toLocaleString('en-GB', {
                                            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                                        })}
                                    </Typography>
                                )}
                                {chargerStatus.error_message && (
                                    <Typography variant="caption" color="error.main" display="block" sx={{ wordBreak: 'break-word' }}>
                                        {chargerStatus.error_message}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="medium">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#424242' }}>
                                        Site
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#424242' }}>
                                        Charger
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#424242' }}>
                                        {translate('resources.firmware-updates.charger_status.status')}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#424242' }}>
                                        {translate('resources.firmware-updates.charger_status.last_updated')}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#424242' }}>
                                        {translate('resources.firmware-updates.charger_status.error_message')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {record.charger_statuses.map((chargerStatus: any, index: number) => (
                                    <TableRow
                                        key={chargerStatus.charger_id}
                                        sx={{
                                            '&:nth-of-type(odd)': {
                                                backgroundColor: '#FAFAFA',
                                            },
                                            '&:hover': {
                                                backgroundColor: '#F0F0F0',
                                            },
                                        }}
                                    >
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {chargerStatus.charger?.sites?.name || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {chargerStatus.charger?.name || chargerStatus.charger_id}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <StatusChip status={chargerStatus.status || 'pending'} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {chargerStatus.last_status_update_at
                                                    ? new Date(chargerStatus.last_status_update_at).toLocaleString('en-GB', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                      })
                                                    : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {chargerStatus.error_message ? (
                                                <Typography
                                                    variant="body2"
                                                    color="error.main"
                                                    sx={{
                                                        maxWidth: 300,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    title={chargerStatus.error_message}
                                                >
                                                    {chargerStatus.error_message}
                                                </Typography>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    -
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
};

// Page Header Component
const PageHeader = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const translate = useTranslate();
    const refresh = useRefresh();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    if (!record) return null;

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mb: { xs: 2, sm: 4 },
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                <IconButton
                    onClick={() => redirect('/firmware-updates')}
                    size="large"
                    sx={{
                        flexShrink: 0,
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        },
                    }}
                >
                    <ArrowBack />
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {translate('resources.firmware-updates.show.title')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                        {record.site_names && record.site_names.length > 0
                            ? record.site_names.join(', ') + ' • '
                            : ''}
                        {record.firmware?.vendor || ''} {record.firmware?.model || ''} • v{record.firmware?.version || ''} • {record.total_chargers || 0} chargers
                    </Typography>
                </Box>
            </Box>
            {isMobile ? (
                <IconButton
                    onClick={() => refresh()}
                    sx={{
                        flexShrink: 0,
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        },
                    }}
                >
                    <Refresh />
                </IconButton>
            ) : (
                <Button
                    onClick={() => refresh()}
                    startIcon={<Refresh />}
                    variant="outlined"
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                    }}
                >
                    {translate('ra.action.refresh')}
                </Button>
            )}
        </Box>
    );
};

const FirmwareUpdatesShow = () => {
    const translate = useTranslate();

    return (
        <Show
            title={false}
            actions={false}
            component="div"
            sx={{
                maxWidth: '100%',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                '& .RaShow-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleShowLayout
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto' }}>
                    <PageHeader />

                    {/* Firmware Information Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Memory sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.firmware-updates.sections.firmware_selection')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '100px 1fr', sm: '150px 1fr' }, gap: 2, alignItems: 'start' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
                                    {translate('resources.firmware-updates.fields.firmware_version')}
                                </Typography>
                                <FunctionField
                                    render={(record: any) => (
                                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#00C853' }}>
                                            {record.firmware?.version || '-'}
                                        </Typography>
                                    )}
                                />

                                <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
                                    {translate('resources.firmware-updates.fields.vendor')}
                                </Typography>
                                <FunctionField
                                    render={(record: any) => (
                                        <Typography variant="body1">
                                            {record.firmware?.vendor || '-'}
                                        </Typography>
                                    )}
                                />

                                <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
                                    {translate('resources.firmware-updates.fields.model')}
                                </Typography>
                                <FunctionField
                                    render={(record: any) => (
                                        <Typography variant="body1">
                                            {record.firmware?.model || '-'}
                                        </Typography>
                                    )}
                                />

                                <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
                                    {translate('resources.firmware-updates.fields.firmware_description')}
                                </Typography>
                                <FunctionField
                                    render={(record: any) => (
                                        <Typography variant="body2" color="text.secondary">
                                            {record.firmware?.release_notes || '-'}
                                        </Typography>
                                    )}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Target Selection Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Devices sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.firmware-updates.sections.target_selection')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '100px 1fr', sm: '150px 1fr' }, gap: 2, alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {translate('resources.firmware-updates.fields.target_type')}
                                </Typography>
                                <FunctionField
                                    render={(record: any) => (
                                        <Chip
                                            label={translate(`resources.firmware-updates.target_types.${record.target_type}`)}
                                            size="small"
                                            sx={{
                                                backgroundColor: '#E3F2FD',
                                                color: '#1976D2',
                                                fontWeight: 500,
                                            }}
                                        />
                                    )}
                                />

                                <Typography variant="body2" color="text.secondary">
                                    Sites
                                </Typography>
                                <FunctionField
                                    render={(record: any) => (
                                        <Typography variant="body1">
                                            {record.site_names && record.site_names.length > 0
                                                ? record.site_names.join(', ')
                                                : '-'}
                                        </Typography>
                                    )}
                                />

                                <Typography variant="body2" color="text.secondary">
                                    {translate('resources.firmware-updates.fields.total_chargers')}
                                </Typography>
                                <FunctionField
                                    render={(record: any) => (
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {record.total_chargers || 0}
                                        </Typography>
                                    )}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Status & Progress Section */}
                    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Assessment sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.firmware-updates.sections.status')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '100px 1fr', sm: '150px 1fr' }, gap: 2, alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary">
                                    {translate('resources.firmware-updates.fields.status')}
                                </Typography>
                                <FunctionField
                                    render={(record: any) => <StatusChip status={record.status || 'pending'} />}
                                />

                                <Typography variant="body2" color="text.secondary">
                                    {translate('resources.firmware-updates.fields.progress')}
                                </Typography>
                                <FunctionField
                                    render={(record: any) => (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#00C853' }}>
                                                {record.completed_count || 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                / {record.total_chargers || 0}
                                            </Typography>
                                            {record.failed_count > 0 && (
                                                <Chip
                                                    label={`${record.failed_count} failed`}
                                                    size="small"
                                                    color="error"
                                                    sx={{ ml: 1 }}
                                                />
                                            )}
                                        </Box>
                                    )}
                                />

                                <Typography variant="body2" color="text.secondary">
                                    {translate('resources.firmware-updates.fields.created_at')}
                                </Typography>
                                <FunctionField
                                    render={(record: any) => (
                                        <Typography variant="body2">
                                            {record.created_at
                                                ? new Date(record.created_at).toLocaleString('en-GB', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                  })
                                                : '-'}
                                        </Typography>
                                    )}
                                />

                                <Typography variant="body2" color="text.secondary">
                                    {translate('resources.firmware-updates.fields.updated_at')}
                                </Typography>
                                <FunctionField
                                    render={(record: any) => (
                                        <Typography variant="body2">
                                            {record.updated_at
                                                ? new Date(record.updated_at).toLocaleString('en-GB', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                  })
                                                : '-'}
                                        </Typography>
                                    )}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Charger Status Table */}
                    <ChargerStatusTable />
                </Box>
            </SimpleShowLayout>
        </Show>
    );
};

export default FirmwareUpdatesShow;
